import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LoginService } from '../../shared/api/login.service';
import { LoginMethod, PasswordLoginMethod } from '../../shared/api/types';
import { RecaptchaService } from '../../shared/recaptcha.service';
import { SavedUsernameService } from '../../shared/saved-username.service';
import {
  PasswordLoginChallengeParams,
  PasswordLoginChallengeComponent,
} from './password/password-login-challenge.component';
import {
  FormErrors,
  KaisatsuResponseError,
} from '../../shared/api/response.error';
import { GlobalMessageService } from '../../shared/global-message.service';
import { environment } from '../../../environments/environment';
import { kaisatsuHandleError } from '../../shared/kaisatsu-error-handler';
import { Title } from '@angular/platform-browser';
import { LoginChallengeService } from '../../shared/login-challenge.service';
import { BackurlService } from '../../shared/backurl.service';
import { isThirdPartyLoginUrl } from '../../shared/is-third-party-login-url';
import { PasswordResetInfoService } from '../../shared/password-reset-info.service';
import { HeaderComponent } from '../../shared/header/header.component';

import { GlobalMessageComponent } from '../../shared/global-message/global-message.component';
import { SpenLabel, SpenLink, SpenHeading } from '@classi/spen';
import { FooterComponent } from '../../shared/footer/footer.component';

@Component({
  selector: 'app-login-challenge',
  templateUrl: './login-challenge.component.html',
  styleUrls: ['./login-challenge.component.scss'],
  imports: [
    HeaderComponent,
    GlobalMessageComponent,
    SpenLabel,
    SpenLink,
    RouterLink,
    SpenHeading,
    PasswordLoginChallengeComponent,
    FooterComponent,
  ],
})
export class LoginChallengeComponent implements OnInit {
  username = '';
  password = '';
  loginMethods: LoginMethod[] = [];
  formErrors: FormErrors | null = null;
  submitInProgress = false;
  isThirdPartyLogin = false;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly loginChallengeService: LoginChallengeService,
    private readonly loginService: LoginService,
    private readonly savedIdService: SavedUsernameService,
    private readonly recaptchaService: RecaptchaService,
    private readonly globalMessageService: GlobalMessageService,
    private readonly titleService: Title,
    private readonly backurlService: BackurlService,
    private readonly passwordResetInfoService: PasswordResetInfoService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.titleService.setTitle(this.route.snapshot.data.title);

    const { username, password, loginMethods } =
      this.loginChallengeService.loginChallengeInfo();
    if (username === null) {
      this.router.navigate(['/login/identifier'], {
        queryParamsHandling: 'preserve',
      });
      return;
    }
    this.username = username;
    this.password = password ?? '';
    this.loginMethods = loginMethods;

    // パスワードリセットに進んだ場合に使用するのでusernameを保存
    this.passwordResetInfoService.set(this.username);

    // エラーメッセージは毎回リセットする
    this.formErrors = null;

    // backurlが連携サービスへのログインURLかどうかを検証する
    const backurl = this.backurlService.get();
    if (backurl) {
      this.isThirdPartyLogin = isThirdPartyLoginUrl(backurl);
    }
  }

  getPasswordLoginMethod(): PasswordLoginMethod | null {
    function isPasswordLoginMethod(
      loginMethod: LoginMethod
    ): loginMethod is PasswordLoginMethod {
      return loginMethod.name === 'password';
    }

    return this.loginMethods.find(isPasswordLoginMethod) ?? null;
  }

  async onPasswordLoginChallenge(
    params: PasswordLoginChallengeParams
  ): Promise<void> {
    this.submitInProgress = true;
    this.globalMessageService.delete();
    // 次回からのID入力省略にチェックがある場合は保存、ない場合は保存されているものを消す
    params.saveId
      ? this.savedIdService.setId(this.username)
      : this.savedIdService.removeId();

    try {
      await this.loginService.postLoginWithPassword(this.username, params);
      this.router.navigateByUrl('/login/continue');
    } catch (e: KaisatsuResponseError | unknown) {
      // reCAPTCHA Widgetをリセットする
      this.recaptchaService.reset();
      if (e instanceof KaisatsuResponseError) {
        // グローバルに表示するエラーメッセージをここで保存
        this.globalMessageService.setErrorMessage(e.messages);
        switch (e.status) {
          // not_foundエラーの場合は/login/identifierに戻す
          case 404:
            this.router.navigateByUrl('/login/identifier');
            break;
        }
        // フォームに関するエラーがある場合はフォームに渡す
        if (e.messages?.form) {
          this.formErrors = e.messages.form;
        }
      } else {
        kaisatsuHandleError(e, this.globalMessageService);
      }
    }
    this.submitInProgress = false;
    this.cdr.markForCheck();
  }
}
